/* eslint-disable react/jsx-key */
import type { MouseEvent, ReactNode, SyntheticEvent, WheelEvent } from 'react';
import React, { Component } from 'react';
import Grid from './grid.react';
import { Labels } from './image-label.react';
import { adjustOffsetsForReference, calculateScaleOffsetToFit } from './utils';
type $Partial = any;
export type ReferenceMouseEvent = {
  referenceX: number;
  referenceY: number;
  referenceMovementX: number;
  referenceMovementY: number;
  // @ts-expect-error todo(ts-migration) TS2552 Cannot find name '$Partial'. Did you mean 'Partial'?
} & $Partial<MouseEvent<HTMLElement>>;
type ImageObject = {
  type: 'image';
  url: string;
  rotation: number;
  x?: number;
  y?: number;
};
export type MoveDirection = 'top-left' | 'top-right' | 'bottom-right' | 'bottom-left' | 'top' | 'left' | 'right' | 'bottom';
export const LineType = Object.freeze({
  DASH: 'dash',
  SOLID: 'solid'
});
export type BoundingInput = {
  x: number;
  y: number;
  height: number;
  width: number;
  drawMode?: string;
};
type BoundingBoxProperties = {
  type: 'boundingBox';
  color: string;
  lineType: (typeof LineType)[keyof typeof LineType];
};
export type BoundingEllipse = {
  cx: number;
  cy: number;
  rx: number;
  ry: number;
};
type BoundingEllipseProperties = {
  type: 'boundingEllipse';
  color: string;
  lineType: (typeof LineType)[keyof typeof LineType];
};
type BoundingBoxInput = BoundingInput & BoundingBoxProperties;
type BoundingEllipseInput = BoundingInput & BoundingEllipseProperties;
export type LabelInput = BoundingBoxInput | BoundingEllipseInput;
export type EditorObject = ImageObject | LabelInput;
export type ImageSize = {
  height: number;
  width: number;
};
export type CanvasOffset = {
  x: number;
  y: number;
};

// TODO: Replace objects array with children React type components
export type Props = {
  scale: number;
  objects: EditorObject[];
  offsets: CanvasOffset;
  backgroundColor: string;
  onReferenceMouseUp: (e: ReferenceMouseEvent) => void;
  onReferenceMouseDown: (e: ReferenceMouseEvent) => void;
  onReferenceMouseMove: (e: ReferenceMouseEvent) => void;
  onUpdateImageSize: (e: ImageSize) => void;
  gridEnabled: boolean;
  gridColor: string;
  selectedAnnotation?: number;
  onAnnotationClick: (e: ReferenceMouseEvent, idx: number) => void;
  onVertexClick: (e: ReferenceMouseEvent, direction: MoveDirection) => void;
  onScrollScale?: ((newOffsets: CanvasOffset, newScale: number) => void) | null;
};
type State = {
  imgSize: ImageSize;
};
const MIN_SCALE = 0.1;
const SCROLL_ZOOM_DEADBAND = 4;
const SCROLL_ZOOM_GAIN = 0.005;
class EditorCanvas extends Component<Props, State> {
  static defaultProps = {
    scale: 1,
    offsets: {
      x: 0,
      y: 0
    },
    backgroundColor: 'black',
    objects: [],
    onReferenceMouseUp: () => {},
    onReferenceMouseDown: () => {},
    onReferenceMouseMove: () => {},
    onUpdateImageSize: () => {},
    gridEnabled: true,
    gridColor: 'gray',
    onAnnotationClick: () => {},
    onVertexClick: () => {}
  };
  containerRef = React.createRef<HTMLDivElement>();
  resizeObserver: ResizeObserver | undefined | null;
  constructor(props: Props) {
    super(props);
    this.state = {
      imgSize: {
        height: 0,
        width: 0
      }
    };
  }
  componentDidMount() {
    this.resizeObserver = new ResizeObserver(entries => {
      const {
        imgSize
      } = this.state;
      if (imgSize.width !== 0 && imgSize.height) {
        this.autoFitImageInContainer();
      }
    });
    this.containerRef.current && this.resizeObserver.observe(this.containerRef.current);
  }
  componentDidUpdate(prevProps: Props, prevState: State) {
    const prevRotation = prevProps.objects.find(obj => obj?.type === 'image')?.rotation;
    const currentRotation = this.props.objects.find(obj => obj?.type === 'image')?.rotation;
    if (currentRotation !== prevRotation || prevState.imgSize !== this.state.imgSize) {
      this.autoFitImageInContainer();
    }
  }
  componentWillUnmount() {
    this.resizeObserver?.disconnect();
  }
  autoFitImageInContainer() {
    const {
      onScrollScale,
      objects
    } = this.props;
    const containerSize = this.containerRef.current?.getBoundingClientRect();
    const rotation = objects.find(obj => obj?.type === 'image')?.rotation;
    let imgSize = {
      ...this.state.imgSize
    };
    if (rotation === 90 || rotation === 270) {
      imgSize = {
        width: this.state.imgSize.height,
        height: this.state.imgSize.width
      };
    }
    if (containerSize && containerSize.width !== 0 && containerSize.height !== 0) {
      const {
        scale,
        offset
      } = calculateScaleOffsetToFit(imgSize, containerSize);
      onScrollScale && onScrollScale(offset, scale);
    }
  }
  onImgLoad = (e: SyntheticEvent<HTMLImageElement>) => {
    const {
      onUpdateImageSize
    } = this.props;
    const imgSize = {
      width: e.currentTarget.width,
      height: e.currentTarget.height
    };
    onUpdateImageSize(imgSize);
    this.setState({
      imgSize
    });
  };
  renderImage(obj: ImageObject) {
    /*
      ojbectFit: none, prevents scaling
      objectPosition, renders the image at the top left
      Some style from Ub
      we need to set it to 'none' because otheer Base is setting the image maxWidth to 100%,
      rwise the component with collapse to 0 width.
    */
    const objStyle = {
      position: 'absolute',
      top: obj.y || 0,
      left: obj.x || 0
    };
    const {
      imgSize
    } = this.state;
    let transform = `rotate(${obj.rotation}deg)`;
    if (obj.rotation % 180 !== 0) {
      const offset = (imgSize.width - imgSize.height) / 2;
      transform = `translate(${-offset}px, ${offset}px) rotate(${obj.rotation}deg)`;
    }
    return (
      // @ts-expect-error todo(ts-migration) TS2322 Type '{ position: string; top: number; left: number; }' is not assignable to type 'CSSProperties'.
      <div style={objStyle}>
        <img onLoad={this.onImgLoad} draggable={false} style={{
          objectFit: 'none',
          objectPosition: '0px 0px',
          maxWidth: 'none',
          transform
        }} src={obj.url} />
      </div>
    );
  }
  scaleFn(n: number) {
    return n * this.props.scale;
  }
  unScaleFn(n: number) {
    return n / this.props.scale;
  }

  /*
   * Returns the corners and mid-point vertices of a rectangle
   */
  getBoundingBoxVertices({
    x,
    y,
    width,
    height
  }: BoundingInput): Array<[number, number, MoveDirection]> {
    return [[x, y, 'top-left'], [x + width / 2, y, 'top'], [x + width, y, 'top-right'], [x + width, y + height / 2, 'right'], [x + width, y + height, 'bottom-right'], [x + width / 2, y + height, 'bottom'], [x, y + height, 'bottom-left'], [x, y + height / 2, 'left']];
  }
  renderBoundingBoxVertices(bbox: BoundingInput): ReactNode {
    const {
      onVertexClick
    } = this.props;
    const vertices = this.getBoundingBoxVertices(bbox);
    return vertices.map((v: [number, number, MoveDirection]) => <circle data-testid="bounding-box-vertex" onMouseDown={e => {
      // @ts-expect-error todo(ts-migration) TS2345 Argument of type 'MouseEvent<SVGCircleElement, MouseEvent>' is not assignable to parameter of type 'MouseEvent<HTMLElement, MouseEvent>'.
      onVertexClick(this.tranformMouseEventForReference(e), v[2]);
    }} cx={v[0]} cy={v[1]} fill="white" strokeWidth="1" stroke="black" r="5" />);
  }
  getBoundingEllipseVertices({
    cx,
    cy,
    rx,
    ry
  }: BoundingEllipse): Array<[number, number, MoveDirection]> {
    return [[cx - rx, cy - ry, 'top-left'], [cx, cy - ry, 'top'], [cx + rx, cy - ry, 'top-right'], [cx + rx, cy, 'right'], [cx + rx, cy + ry, 'bottom-right'], [cx, cy + ry, 'bottom'], [cx - rx, cy + ry, 'bottom-left'], [cx - rx, cy, 'left']];
  }
  renderBoundingEllipseVertices(bbox: BoundingEllipse): ReactNode {
    const {
      onVertexClick
    } = this.props;
    const vertices = this.getBoundingEllipseVertices(bbox);
    return vertices.map((v: [number, number, MoveDirection]) => <circle data-testid="bounding-ellipse-vertex" onMouseDown={e => {
      // @ts-expect-error todo(ts-migration) TS2345 Argument of type 'MouseEvent<SVGCircleElement, MouseEvent>' is not assignable to parameter of type 'MouseEvent<HTMLElement, MouseEvent>'.
      onVertexClick(this.tranformMouseEventForReference(e), v[2]);
    }} cx={v[0]} cy={v[1]} fill="white" strokeWidth="1" stroke="black" r="5" />);
  }
  renderBoundingBox(obj: LabelInput, idx: number) {
    const {
      offsets,
      selectedAnnotation,
      onAnnotationClick
    } = this.props;
    const boxStyle = {
      fill: 'none',
      strokeWidth: selectedAnnotation === idx ? 5 : 3,
      stroke: obj.color || 'black',
      strokeDasharray: obj.lineType === LineType.SOLID ? '0' : '10 5'
    };
    const transparentBboxStyle = {
      ...boxStyle,
      strokeWidth: 15,
      strokeDasharray: 0,
      stroke: 'none',
      strokeOpacity: 0.4,
      pointerEvents: 'stroke'
    };
    const bbox = {
      x: this.scaleFn(obj.x) + this.scaleFn(offsets.x),
      y: this.scaleFn(obj.y) + this.scaleFn(offsets.y),
      width: this.scaleFn(obj.width),
      height: this.scaleFn(obj.height)
    };
    return <g data-testid={`boundingBox-${idx}`}>
        <rect style={boxStyle} {...bbox} />
        <rect data-testid={`boundingBoxComp-${idx}`}
      // @ts-expect-error todo(ts-migration) TS2322 Type '{ strokeWidth: number; strokeDasharray: number; stroke: string; strokeOpacity: number; pointerEvents: string; fill: string; }' is not assignable to type 'CSSProperties'.
      style={transparentBboxStyle} onMouseDown={e => {
        // @ts-expect-error todo(ts-migration) TS2345 Argument of type 'MouseEvent<SVGRectElement, MouseEvent>' is not assignable to parameter of type 'MouseEvent<HTMLElement, MouseEvent>'.
        onAnnotationClick(this.tranformMouseEventForReference(e), idx);
      }} {...bbox} />
        {selectedAnnotation === idx && this.renderBoundingBoxVertices(bbox)}
      </g>;
  }
  renderBoundingEllipse(obj: LabelInput, idx: number) {
    const {
      offsets,
      selectedAnnotation,
      onAnnotationClick
    } = this.props;
    const ellipseStyle = {
      fill: 'none',
      strokeWidth: selectedAnnotation === idx ? 5 : 3,
      stroke: obj.color || 'black',
      strokeDasharray: obj.lineType === LineType.SOLID ? '0' : '10 5'
    };
    const transparentBellipseStyle = {
      ...ellipseStyle,
      strokeWidth: 15,
      strokeDasharray: 0,
      stroke: 'none',
      strokeOpacity: 0.4,
      pointerEvents: 'stroke'
    };
    const bellipse = {
      cx: this.scaleFn(obj.x) + this.scaleFn(offsets.x) + this.scaleFn(obj.width / 2),
      cy: this.scaleFn(obj.y) + this.scaleFn(offsets.y) + this.scaleFn(obj.height / 2),
      rx: this.scaleFn(obj.width / 2),
      ry: this.scaleFn(obj.height / 2)
    };
    return <g data-testid={`boundingEllipse-${idx}`}>
        <ellipse style={ellipseStyle} {...bellipse} />
        <ellipse
      // @ts-expect-error todo(ts-migration) TS2322 Type '{ strokeWidth: number; strokeDasharray: number; stroke: string; strokeOpacity: number; pointerEvents: string; fill: string; }' is not assignable to type 'CSSProperties'.
      style={transparentBellipseStyle} data-testid={`boundingEllipseComp-${idx}`} onMouseDown={e => {
        // @ts-expect-error todo(ts-migration) TS2345 Argument of type 'MouseEvent<SVGEllipseElement, MouseEvent>' is not assignable to parameter of type 'MouseEvent<HTMLElement, MouseEvent>'.
        onAnnotationClick(this.tranformMouseEventForReference(e), idx);
      }} {...bellipse} />
        {selectedAnnotation === idx && this.renderBoundingEllipseVertices(bellipse)}
      </g>;
  }
  renderObject = (obj: EditorObject) => {
    if (!obj) {
      return null;
    }
    if (obj.type === 'image') {
      return this.renderImage(obj);
    }
    return null;
  };
  renderSVGObject = (obj: LabelInput, idx: number) => {
    if (!obj) {
      return null;
    }
    if (obj.drawMode === Labels.ellipse) {
      return this.renderBoundingEllipse(obj, idx);
    }
    return this.renderBoundingBox(obj, idx);
  };
  tranformMouseEventForReference: (e: MouseEvent<HTMLElement>) => ReferenceMouseEvent = (e: MouseEvent<HTMLElement>) => {
    /*
      Add unscale and adjust offsets to mouse events
    */
    const container = e.currentTarget.getBoundingClientRect();
    return {
      ...e,
      referenceX: this.unScaleFn(e.clientX - container.left) - this.props.offsets.x,
      referenceY: this.unScaleFn(e.clientY - container.top) - this.props.offsets.y,
      // Flow doesn't have movementX/Y for some reason?
      /* $FlowFixMe This comment suppresses an error found when enabling type
       * checking of the legacy common/shared/ packages in the monorepo. To
       * view the error, delete this comment and run Flow. */
      referenceMovementX: this.unScaleFn(e.movementX),
      /* $FlowFixMe This comment suppresses an error found when enabling type
       * checking of the legacy common/shared/ packages in the monorepo. To
       * view the error, delete this comment and run Flow. */
      referenceMovementY: this.unScaleFn(e.movementY)
    };
  };
  handleWheel = (e: WheelEvent<HTMLElement>) => {
    if (!this.props.onScrollScale) {
      return;
    }
    const delta = e.deltaY;
    if (Math.abs(delta) < SCROLL_ZOOM_DEADBAND) {
      return;
    }

    // Adjust offsets to mouse events
    const container = e.currentTarget.getBoundingClientRect();
    this.adjustScale({
      x: e.clientX - container.left,
      y: e.clientY - container.top
    }, delta * SCROLL_ZOOM_GAIN);
  };
  adjustScale = (anchor: {
    x: number;
    y: number;
  }, amount: number) => {
    const {
      scale: scaleCurrent,
      offsets: currentOffsets,
      onScrollScale
    } = this.props;
    const scaleNew = Math.max(MIN_SCALE, scaleCurrent * (1 + amount));
    const offsetsNew = adjustOffsetsForReference(anchor, scaleCurrent, scaleNew, currentOffsets);
    onScrollScale && onScrollScale(offsetsNew, scaleNew);
  };
  render() {
    const {
      backgroundColor,
      objects,
      scale,
      onReferenceMouseMove,
      onReferenceMouseDown,
      onReferenceMouseUp,
      offsets,
      gridEnabled,
      gridColor
    } = this.props;
    return <div style={{
      height: '100%',
      width: '100%',
      backgroundColor,
      position: 'absolute',
      overflow: 'hidden'
    }} ref={this.containerRef} onMouseMove={(e: MouseEvent<HTMLElement>) => onReferenceMouseMove(this.tranformMouseEventForReference(e))} onMouseUp={(e: MouseEvent<HTMLElement>) => onReferenceMouseUp(this.tranformMouseEventForReference(e))} onMouseDown={(e: MouseEvent<HTMLElement>) => onReferenceMouseDown(this.tranformMouseEventForReference(e))} onWheel={this.handleWheel}>
        <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        transform: `translate(${offsets.x}px, ${offsets.y}px) scale(${scale})`,
        transformOrigin: `${-offsets.x}px ${-offsets.y}px`
      }}>
          {objects.map(this.renderObject)}
        </div>
        <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        height: '100%',
        width: '100%'
      }}>
          <svg height="100%" width="100%">
            {gridEnabled ? <Grid color={gridColor} /> : null}
            {objects.map(this.renderSVGObject)}
          </svg>
        </div>
      </div>;
  }
}
export default EditorCanvas;